
import { useState } from "react";
import { Sparkles, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

interface DailyTipsButtonProps {
  dataToAnalyze: any;
  context: "cycle" | "pregnancy";
}

export const DailyTipsButton = ({ dataToAnalyze, context }: DailyTipsButtonProps) => {
  const { toast } = useToast();
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [tips, setTips] = useState<string>("");

  const getTips = async () => {
    setIsLoading(true);
    setTips("");
    try {
      const { data, error } = await supabase.functions.invoke("generate-daily-tips", {
        body: { 
          context,
          userData: dataToAnalyze 
        }
      });

      if (error) throw error;
      
      setTips(data.tips);
    } catch (error) {
      console.error("Erreur lors de la génération des conseils:", error);
      toast({
        title: "Erreur",
        description: "Impossible de générer des conseils pour le moment. Veuillez réessayer plus tard.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleClick = () => {
    setIsOpen(true);
    getTips();
  };

  return (
    <>
      <Button 
        onClick={handleClick} 
        className="flex items-center gap-2 group transition-all duration-300" 
        variant={context === "cycle" ? "purple" : "default"}
      >
        <Sparkles className="h-4 w-4 group-hover:animate-pulse" />
        <span>Conseils du jour</span>
      </Button>

      <Dialog open={isOpen} onOpenChange={setIsOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Sparkles className="h-5 w-5 text-primary" />
              Conseils personnalisés du jour
            </DialogTitle>
            <DialogDescription>
              {context === "cycle" 
                ? "Conseils basés sur vos données de cycle menstruel" 
                : "Conseils adaptés à votre stade de grossesse"}
            </DialogDescription>
          </DialogHeader>

          <div className="mt-4 space-y-4">
            {isLoading ? (
              <div className="flex flex-col items-center justify-center py-8">
                <Loader2 className="h-8 w-8 animate-spin text-primary mb-2" />
                <p className="text-muted-foreground text-sm">Analyse en cours...</p>
              </div>
            ) : (
              <div className="whitespace-pre-wrap bg-secondary/30 p-4 rounded-lg border">
                {tips.split('\n').map((paragraph, index) => (
                  <p key={index} className={index > 0 ? "mt-2" : ""}>
                    {paragraph}
                  </p>
                ))}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};
